import React, { useState } from 'react';
import VoxelDialog from '../../../components/voxel/Dialogs/VoxelDialog';
import { TeamConnectionGq } from '../../../graphql/users/models/TeamConnectionGq';
import { usersApiService } from '../../../graphql/users/usersApiService';
import VoxelDialogContent from '../../../components/voxel/Dialogs/VoxelDialogContent';
import moment from 'moment';
import VoxelInput from '../../../components/voxel/Forms/VoxelInput';
import VoxelButton from '../../../components/voxel/Buttons/VoxelButton';
import VoxelTable from '../../../components/voxel/Tables/VoxelTable';
import VoxelTableRow from '../../../components/voxel/Tables/VoxelTableRow';
import VoxelTableHeadCell from '../../../components/voxel/Tables/VoxelTableHeadCell';
import VoxelTableCell from '../../../components/voxel/Tables/VoxelTableCell';
import VoxelAvatar from '../../../components/voxel/VoxelAvatar';
import { orderBy } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/pro-regular-svg-icons';

export interface ResendConnectionInvitationDialogProps {
  connection: TeamConnectionGq;
  onSuccess: () => Promise<unknown>;
  close: () => void;
  preventSpam: boolean;
}

export const ResendConnectionInvitationDialog: React.FC<ResendConnectionInvitationDialogProps> = ({
  connection,
  onSuccess,
  close,
  preventSpam,
}) => {
  const resendMutation = usersApiService.mutations.useSendConnectionInvitationMail();
  const [email, setEmail] = useState('');

  const handleClose = () => {
    setEmail('');
    close();
  };

  const handleSubmit = () => {
    return resendMutation({ sendToEmail: email, teamConnectionId: connection.id })
      .then(onSuccess)
      .then(() => setEmail(''));
  };

  if (!connection) return null;

  return (
    <VoxelDialog onClose={handleClose} open title="Resend connection invitation">
      <VoxelDialogContent classMinWidth="min-w-[600px]">
        <div className="flex flex-col gap-8">
          <div className="w-full">
            <form
              onSubmit={(event) => {
                event.stopPropagation();
                event.preventDefault();
              }}
              className="w-full flex flex-row items-end gap-2 pt-2"
            >
              <div className="grow">
                <VoxelInput
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  label="Collaborator's email address"
                  labelPosition="top"
                  placeholder="john.doe@gmail.com"
                  inputWidthClass="w-full"
                />
              </div>
              <VoxelButton
                title="Resend invitation"
                disabled={preventSpam || !email?.length}
                onClick={handleSubmit}
                color="primary"
                shape="square"
                type="submit"
                size="xl"
                endIcon={<FontAwesomeIcon icon={faPaperPlane} />}
              />
            </form>
          </div>
          <div>
            <VoxelTable>
              <thead>
                <VoxelTableRow>
                  <VoxelTableHeadCell className="pr-2">Previously sent to</VoxelTableHeadCell>
                  <VoxelTableHeadCell align="center">Sent by</VoxelTableHeadCell>
                  <VoxelTableHeadCell className="pl-2">Send time</VoxelTableHeadCell>
                  <VoxelTableHeadCell className="pl-2">Expires</VoxelTableHeadCell>
                </VoxelTableRow>
              </thead>
              <tbody>
                {orderBy(connection.invitations ?? [], (i) => i.sentTime).map((invitation) => (
                  <VoxelTableRow key={invitation.id}>
                    <VoxelTableCell className="pr-2 select-all">
                      {invitation.sentToEmail ?? '?'}
                    </VoxelTableCell>
                    <VoxelTableCell align="center">
                      <VoxelAvatar name={invitation.sentByUser?.email ?? '?'} tooltip={invitation.sentByUser?.email} />
                    </VoxelTableCell>
                    <VoxelTableCell className="pl-2">
                      {' '}
                      {moment.utc(invitation.sentTime).format('MMM Do, YY')}
                    </VoxelTableCell>
                    <VoxelTableCell className="pl-2">
                      {' '}
                      {moment.utc(invitation.tokenExpirationTime).fromNow()}
                    </VoxelTableCell>
                  </VoxelTableRow>
                ))}
              </tbody>
            </VoxelTable>
          </div>
        </div>
      </VoxelDialogContent>
    </VoxelDialog>
  );
};

export default ResendConnectionInvitationDialog;
